'use client';

// СТАТЬЯ СПРАВКИ — ОДИН КОМПОНЕНТ НА ВСЕ МЕСТА (ТЗ ~/projects/HELP-ARTICLE-UNIFY-TZ.md, владелец 25.10.2026: «один
// компонент, чтобы я сказал — другой заголовок подставить, отступы изменить — и это происходило сразу во всех местах»).
// Рисует статью ВЕЗДЕ, где её открывают на сайте:
//   • place='page'  — публичная страница /<язык>/help/<раздел>/<статья> (каркас сайта: логотип, язык — HelpShell):
//     крошки «Справка › Раздел» и «поделиться» над заголовком, затем большой заголовок и текст. Та же страница в голом
//     режиме (`?app=1&bare=1`) — островок статьи приложения: крошек и «поделиться» нет (help.css, data-help-bare),
//     шапку рисует натив.
//   • place='sheet' — окно кабинета (стопка на телефоне, панель 460 на широком экране; справка, пасхалка «Ботов»,
//     «Создать бота» → «Пошаговые инструкции», ссылки внутри статей): шапку «× · короткий заголовок · поделиться»
//     даёт окно (help-layer.tsx `openArticleSheet`), тело — отсюда. Данные — оглавление с телами статей
//     (lib/help-article-data.ts), прогрето заранее.
// Три заголовка — `articleTitles` (lib/help-titles.ts). Отступы и шрифты — help.css, контейнерные правила (`helpc`):
// панель кабинета = телефон.
import { useEffect, type MouseEventHandler } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { LegalShell } from '@/components/legal/legal-shell';
import { articleTitles } from '@/lib/help-titles';
import {
  articleOf,
  helpIndexKey,
  helpIndexOpts,
  helpIndexUrl,
  warmHelpArticle,
  type HelpArticleData,
  type HelpIndexReply,
} from '@/lib/help-article-data';
import { usePersistentData } from '@/lib/use-persistent-data';
import { HelpMarkdown } from './help-md';
import { HelpCrumbs } from './help-crumbs';
import { Shape, useShapeRecord } from '@/components/cabinet/skeleton';
import './help.css';

/** Тело статьи: строка «откройте на сайте» (статья про оплату в приложении), большой заголовок, текст. */
function ArticleText({ article }: { article: HelpArticleData }) {
  const t = useTranslations('help');
  return (
    <>
      {article.webOnly ? <p className="help-app-note">{t('appNote')}</p> : null}
      <h1 className="help-a-h1">{articleTitles(article).h1}</h1>
      <div className={article.webOnly ? 'help-art help-web-only' : 'help-art'} data-shape="help-article">
        <div className="help-body" data-shape-sec="art">
          <HelpMarkdown blocks={article.blocks} />
        </div>
      </div>
    </>
  );
}

/** Публичная страница статьи: данные приходят с сервера (страница собрана заранее). */
function ArticlePage({ article, sectionTitle }: { article: HelpArticleData; sectionTitle: string }) {
  const locale = useLocale();
  const t = useTranslations('help');
  return (
    <div className="help-a help-a--page">
      {/* Крошки — общий компонент статьи и раздела; «поделиться» — в шапке сайта (ТЗ HELP-WARM-FIX §6а п.2, п.5). */}
      <div className="help-a-top">
        <HelpCrumbs
          locale={locale}
          helpTitle={t('title')}
          section={sectionTitle ? { slug: article.section, title: sectionTitle } : undefined}
        />
      </div>
      <ArticleText article={article} />
    </div>
  );
}

/** Окно кабинета: данные из общего кэша (прогреты на старте — help-article-data.ts), пока их нет — заглушка формы. */
function ArticleSheet({ section, slug, onLinks }: { section: string; slug: string; onLinks?: MouseEventHandler<HTMLElement> }) {
  const locale = useLocale();
  // Тела статей едут в оглавлении (один ключ, прогрет на входе в кабинет и в местах, откуда статью открывают).
  const { data } = usePersistentData<HelpIndexReply>(helpIndexKey(locale), helpIndexUrl(locale), helpIndexOpts);
  const art = articleOf(data, section, slug);
  // Форма статьи (закон «экран показывается один раз»): снимается при первом показе с текстом, до текста стоит она.
  useShapeRecord('help-article', !!art);
  // Снимки ЭТОЙ статьи и одной следующей по оглавлению — план прогрева (SPEED §6.3: только открытая статья).
  const ready = !!art;
  useEffect(() => {
    if (ready) warmHelpArticle(locale, section, slug);
  }, [ready, locale, section, slug]);
  return (
    <div className="help-layer help-a-host" onClick={onLinks}>
      <LegalShell locale={locale} bare className="help help--art help-a help-a--sheet">
        {art ? <ArticleText article={art} /> : <Shape name="help-article" groupsClass="" groupClass="help-shape" />}
      </LegalShell>
    </div>
  );
}

export function HelpArticle(
  props:
    | { place: 'page'; article: HelpArticleData; sectionTitle: string }
    | { place: 'sheet'; section: string; slug: string; onLinks?: MouseEventHandler<HTMLElement> },
) {
  if (props.place === 'page') return <ArticlePage article={props.article} sectionTitle={props.sectionTitle} />;
  return <ArticleSheet section={props.section} slug={props.slug} {...(props.onLinks ? { onLinks: props.onLinks } : {})} />;
}
